
import { useState } from 'react';
import { AdminLayout } from '@/components/admin/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Search, RefreshCw, FileText, Mail } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';

// Define interface for client data
interface ClientWithRelations {
  id: string;
  profile_id: string;
  application_id: string;
  accepted_quote_id: string;
  provider_id: string;
  onboarding_status: string;
  premium_subscription: boolean;
  created_at: string;
  updated_at: string;
  profile?: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
  };
  provider?: {
    id: string;
    name: string;
  };
  application?: {
    id: string;
    company_legal_name: string | null;
    status: string | null;
  };
  accepted_quote?: {
    id: string;
    monthly_cost: number | null;
    annual_cost: number | null;
    status: string | null;
  };
}

const AdminClients = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();
  
  const { data: clients, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin-clients'],
    queryFn: async () => {
      console.log('Fetching clients from Supabase...');
      
      // First check if the current user is an admin
      const { data: adminCheck, error: adminError } = await supabase.rpc('is_admin_check');
      console.log('Admin check result:', adminCheck, 'Error:', adminError);
      
      if (adminError) {
        console.error('Error checking admin status:', adminError);
        throw new Error(`Admin permission check failed: ${adminError.message}`);
      }
      
      if (!adminCheck) {
        throw new Error('You do not have admin permissions to view client data');
      }
      
      const { data, error } = await supabase
        .from('clients')
        .select(`
          *,
          profile:profile_id (
            id,
            email,
            first_name,
            last_name
          ),
          provider:provider_id (
            id,
            name
          ),
          application:application_id (
            id,
            company_legal_name,
            status
          ),
          accepted_quote:accepted_quote_id (
            id,
            monthly_cost,
            annual_cost,
            status
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching clients:', error);
        throw error;
      }
      
      return data as ClientWithRelations[];
    },
    retry: 1,
    staleTime: 60000 // 1 minute
  });

  const handleRefresh = () => {
    toast({
      title: "Refreshing data",
      description: "Fetching the latest client data"
    });
    refetch();
  };

  const filteredClients = clients?.filter(client => {
    if (!searchQuery) return true;
    
    const searchLower = searchQuery.toLowerCase();
    return (
      client.profile?.email?.toLowerCase().includes(searchLower) ||
      client.profile?.first_name?.toLowerCase().includes(searchLower) ||
      client.profile?.last_name?.toLowerCase().includes(searchLower) ||
      client.provider?.name?.toLowerCase().includes(searchLower) ||
      client.application?.company_legal_name?.toLowerCase().includes(searchLower) ||
      client.onboarding_status?.toLowerCase().includes(searchLower)
    );
  }) || [];

  const recentClients = clients?.filter(client => 
    new Date(client.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  ) || [];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'active':
        return <Badge variant="outline" className="bg-green-100 text-green-800">Active</Badge>;
      case 'inactive':
        return <Badge variant="outline" className="bg-red-100 text-red-800">Inactive</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-3xl font-bold tracking-tight">Clients</h1>
          
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search clients..."
                className="pl-8 w-full sm:w-[300px]"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              size="sm"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        {isError && (
          <Alert variant="destructive">
            <AlertTitle>Error loading clients</AlertTitle>
            <AlertDescription>
              <div>
                {error instanceof Error 
                  ? error.message 
                  : 'An unknown error occurred while fetching clients.'}
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2"
                onClick={handleRefresh}
              >
                Try Again
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {clients?.length === 0 && !isLoading && !isError && (
          <Alert>
            <AlertTitle>No Clients Found</AlertTitle>
            <AlertDescription>
              There are no clients in the database yet. Clients are created when a user accepts a quote.
            </AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="all" className="w-full">
          <TabsList>
            <TabsTrigger value="all">All Clients ({clients?.length || 0})</TabsTrigger>
            <TabsTrigger value="recent">Recently Added ({recentClients.length})</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all" className="mt-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredClients.map(client => (
                <Card key={client.id}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">
                        {client.profile?.first_name} {client.profile?.last_name}
                      </CardTitle>
                      {getStatusBadge(client.onboarding_status)}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {client.application?.company_legal_name || 'No company name'}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="text-sm">
                          <span className="font-medium">Email:</span> {client.profile?.email}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">PEO Provider:</span> {client.provider?.name || 'Not assigned'}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Client since:</span> {formatDistanceToNow(new Date(client.created_at), { addSuffix: true })}
                        </div>
                        {client.accepted_quote?.monthly_cost && (
                          <div className="text-sm">
                            <span className="font-medium">Monthly Cost:</span> ${client.accepted_quote.monthly_cost.toLocaleString()}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" className="gap-1">
                          <FileText className="h-4 w-4" />
                          <span>View Details</span>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="gap-1"
                          onClick={() => window.location.href = `mailto:${client.profile?.email}`}
                        >
                          <Mail className="h-4 w-4" />
                          <span>Contact</span>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="recent" className="mt-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {recentClients.map(client => (
                <Card key={client.id}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">
                        {client.profile?.first_name} {client.profile?.last_name}
                      </CardTitle>
                      {getStatusBadge(client.onboarding_status)}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {client.application?.company_legal_name || 'No company name'}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="text-sm">
                          <span className="font-medium">Email:</span> {client.profile?.email}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">PEO Provider:</span> {client.provider?.name || 'Not assigned'}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Client since:</span> {formatDistanceToNow(new Date(client.created_at), { addSuffix: true })}
                        </div>
                        {client.accepted_quote?.monthly_cost && (
                          <div className="text-sm">
                            <span className="font-medium">Monthly Cost:</span> ${client.accepted_quote.monthly_cost.toLocaleString()}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" className="gap-1">
                          <FileText className="h-4 w-4" />
                          <span>View Details</span>
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="gap-1"
                          onClick={() => window.location.href = `mailto:${client.profile?.email}`}
                        >
                          <Mail className="h-4 w-4" />
                          <span>Contact</span>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminClients;
